import { useState } from "react";
import type { ModerationQueueItem, AdminExperience } from "@/types/admin";
import { getLabel, getEmoji } from "@/utils/taxonomy";
import {
  WHAT_HAPPENED,
  INFLUENCES,
  FEELINGS,
} from "@/shared/taxonomy";
import { useApproveExperience, useRejectExperience, useEditExperience } from "@/hooks/useModerationActions";

interface ModerationDetailModalProps {
  queueItem: ModerationQueueItem;
  experience: AdminExperience;
  onClose: () => void;
}

type Action = null | "approve" | "reject" | "edit";

export function ModerationDetailModal({
  queueItem,
  experience,
  onClose,
}: ModerationDetailModalProps) {
  const [action, setAction] = useState<Action>(null);
  const [note, setNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [editedContext, setEditedContext] = useState(experience.context || "");

  const approveMutation = useApproveExperience();
  const rejectMutation = useRejectExperience();
  const editMutation = useEditExperience();

  const confidencePercent = Math.round(queueItem.confidence * 100);

  function handleApprove() {
    approveMutation.mutate(
      { id: experience.id, note },
      { onSuccess: onClose },
    );
  }

  function handleReject() {
    if (!rejectReason.trim()) return;
    rejectMutation.mutate(
      { id: experience.id, reason: rejectReason, note },
      { onSuccess: onClose },
    );
  }

  function handleEdit() {
    if (!editedContext.trim()) return;
    editMutation.mutate(
      { id: experience.id, sanitizedContext: editedContext, note },
      { onSuccess: onClose },
    );
  }

  const isPending =
    approveMutation.isPending || rejectMutation.isPending || editMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-surface-container rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-stack-md border-b border-white/5">
          <h2 className="font-headline-md text-headline-md text-on-surface">
            Moderation Detail
          </h2>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-stack-md space-y-stack-md">
          {/* AI Analysis */}
          <div className="bg-surface-container-high rounded-xl p-stack-md">
            <div className="flex items-center gap-stack-sm mb-2">
              <span className="font-label-sm text-label-sm text-on-surface-variant">AI Decision:</span>
              <span
                className={`px-2 py-0.5 rounded-full font-label-xs text-label-xs ${
                  queueItem.aiDecision === "ESCALATE"
                    ? "bg-secondary-container text-on-secondary-container"
                    : queueItem.aiDecision === "REJECT"
                      ? "bg-error-container text-on-error-container"
                      : "bg-primary-container text-on-primary-container"
                }`}
              >
                {queueItem.aiDecision}
              </span>
              <span className="font-label-xs text-on-surface-variant">
                {confidencePercent}% confidence
              </span>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {queueItem.reason}
            </p>
          </div>

          {/* Flags */}
          <div className="bg-surface-container-high rounded-xl p-stack-md">
            <p className="font-label-sm text-label-sm text-on-surface-variant mb-2">Flags</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(queueItem.flags ?? {}).map(([key, value]) => {
                if (!value) return null;
                return (
                  <span
                    key={key}
                    className="px-2 py-0.5 bg-secondary-container/30 text-secondary rounded-full font-label-xs text-label-xs"
                  >
                    {key}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Experience Content */}
          <div>
            <p className="font-label-sm text-label-sm text-on-surface-variant mb-2">What Happened</p>
            <div className="flex flex-wrap gap-1.5">
              {experience.whatHappened.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-container-high rounded-full font-label-xs text-label-xs text-on-surface"
                >
                   {getEmoji(WHAT_HAPPENED, id as string)} {getLabel(WHAT_HAPPENED, id as string)}
                </span>
              ))}
            </div>
          </div>

          {experience.influences.length > 0 && (
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-2">Influences</p>
              <div className="flex flex-wrap gap-1.5">
                {experience.influences.map((id) => (
                  <span
                    key={id}
                    className="px-2 py-0.5 bg-surface-container-high rounded-full font-label-xs text-label-xs text-on-surface"
                  >
                     {getLabel(INFLUENCES, id as string)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {experience.feelings.length > 0 && (
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-2">Feelings</p>
              <div className="flex flex-wrap gap-1.5">
                {experience.feelings.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-container-high rounded-full font-label-xs text-label-xs text-on-surface"
                  >
                     {getEmoji(FEELINGS, id as string)} {getLabel(FEELINGS, id as string)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {experience.context && (
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-2">Context (Original)</p>
              <p className="font-body-md text-body-md text-on-surface bg-surface-container-high rounded-xl p-stack-md whitespace-pre-wrap">
                {experience.context}
              </p>
            </div>
          )}

          {/* Meta */}
          <div className="flex flex-wrap gap-stack-sm text-on-surface-variant">
            {experience.companyId && (
              <span className="font-label-xs text-label-xs">Company: {experience.companyId}</span>
            )}
            {experience.function && (
              <span className="font-label-xs text-label-xs">Function: {experience.function}</span>
            )}
            {experience.seniority && (
              <span className="font-label-xs text-label-xs">Seniority: {experience.seniority}</span>
            )}
            {experience.country && (
              <span className="font-label-xs text-label-xs">Country: {experience.country}</span>
            )}
            {experience.cityId && (
              <span className="font-label-xs text-label-xs">City: {experience.cityId}</span>
            )}
          </div>

          {/* Actions */}
          {!action && (
            <div className="flex flex-wrap gap-stack-sm pt-stack-sm border-t border-white/5">
              <button
                onClick={() => setAction("approve")}
                className="px-4 py-2 bg-primary-container text-on-primary-container rounded-lg font-body-md hover:opacity-90 transition-opacity"
              >
                Approve
              </button>
              <button
                onClick={() => setAction("reject")}
                className="px-4 py-2 bg-error-container text-on-error-container rounded-lg font-body-md hover:opacity-90 transition-opacity"
              >
                Reject
              </button>
              <button
                onClick={() => setAction("edit")}
                className="px-4 py-2 bg-surface-container-high text-on-surface rounded-lg font-body-md hover:bg-surface-container-highest transition-colors"
              >
                Edit &amp; Approve
              </button>
            </div>
          )}

          {/* Approve form */}
          {action === "approve" && (
            <div className="space-y-stack-sm pt-stack-sm border-t border-white/5">
              <p className="font-label-sm text-label-sm text-on-surface">Approve this experience?</p>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note..."
                className="w-full bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none"
              />
              <div className="flex gap-stack-sm">
                <button
                  onClick={handleApprove}
                  disabled={isPending}
                  className="px-4 py-2 bg-primary-container text-on-primary-container rounded-lg font-body-md hover:opacity-90 disabled:opacity-50"
                >
                  {isPending ? "Approving..." : "Confirm Approve"}
                </button>
                <button
                  onClick={() => setAction(null)}
                  className="px-4 py-2 text-on-surface-variant font-body-md hover:text-on-surface"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Reject form */}
          {action === "reject" && (
            <div className="space-y-stack-sm pt-stack-sm border-t border-white/5">
              <p className="font-label-sm text-label-sm text-on-surface">Reject this experience?</p>
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Rejection reason (required)..."
                className="w-full bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none"
              />
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note to user..."
                className="w-full bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none"
              />
              <div className="flex gap-stack-sm">
                <button
                  onClick={handleReject}
                  disabled={isPending || !rejectReason.trim()}
                  className="px-4 py-2 bg-error-container text-on-error-container rounded-lg font-body-md hover:opacity-90 disabled:opacity-50"
                >
                  {isPending ? "Rejecting..." : "Confirm Reject"}
                </button>
                <button
                  onClick={() => setAction(null)}
                  className="px-4 py-2 text-on-surface-variant font-body-md hover:text-on-surface"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Edit form */}
          {action === "edit" && (
            <div className="space-y-stack-sm pt-stack-sm border-t border-white/5">
              <p className="font-label-sm text-label-sm text-on-surface">
                Edit context to redact personal information, then approve:
              </p>
              <textarea
                value={editedContext}
                onChange={(e) => setEditedContext(e.target.value)}
                rows={5}
                className="w-full bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none resize-y"
              />
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note..."
                className="w-full bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none"
              />
              <div className="flex gap-stack-sm">
                <button
                  onClick={handleEdit}
                  disabled={isPending || !editedContext.trim()}
                  className="px-4 py-2 bg-primary-container text-on-primary-container rounded-lg font-body-md hover:opacity-90 disabled:opacity-50"
                >
                  {isPending ? "Saving..." : "Save &amp; Approve"}
                </button>
                <button
                  onClick={() => setAction(null)}
                  className="px-4 py-2 text-on-surface-variant font-body-md hover:text-on-surface"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
